import React from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { LEARNOSITY_QUESTION_TYPE_NAMES } from '@utils/constants';
import { StandardRecord } from '@components/ContentFilters/types';
import MathJaxItem from '@components/MathJaxContainer';
import Box from '../Atoms/Box';
import Typography from '../Atoms/Typography';
import Button from '../Atoms/Button/Button';
import Tooltip from '../Atoms/Tooltip';
import CustomCheckbox from './CustomCheckbox';
import { AlgoliaLearnosityItem } from './types';
import QuestionPreview from './QuestionPreview';

interface AlgoliaTableRowProps {
  hit: AlgoliaLearnosityItem;
  showCheckbox: boolean;
  selectedQuestions: string[];
  onQuestionSelect: (questionId: string, selected: boolean) => void;
  selectedTagColumns: string[];
  onTagsOpen: (hit: AlgoliaLearnosityItem) => void;
  currentPage: number;
  pageSize: number;
  rowIndex: number;
  getStandardByGuid: (guid: string) => StandardRecord | null;
  selectButton?: (referenceId: string) => React.ReactNode;
}

/**
 * AlgoliaTableRow component
 * Renders a single row in the algolia table
 */
const AlgoliaTableRow = ({
  hit,
  showCheckbox,
  selectedQuestions,
  onQuestionSelect,
  selectedTagColumns,
  onTagsOpen,
  currentPage,
  pageSize,
  rowIndex,
  getStandardByGuid,
  selectButton = undefined,
}: AlgoliaTableRowProps) => {
  const calculatedIndex = (currentPage * pageSize) + rowIndex + 1;

  if (!hit || !hit.reference) {
    return {
      id: 'invalid-hit',
      checkbox: null,
      item: <Typography variant="body2">Invalid item data</Typography>,
      date: <Typography variant="body2">-</Typography>,
      tags: null,
    };
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionSelect(hit.reference, e.target.checked);
  };

  const renderCheckbox = () => {
    if (!showCheckbox) {
      return null;
    }
    const isUnaligned = !Array.isArray(hit.Skill) || hit.Skill.some((skill: string) =>
      skill.toLowerCase().includes('unaligned'),
    );
    const checkBoxTooltip = isUnaligned
      ? 'This question is missing a skill and cannot be selected for assessments.'
      : 'Select this question to add it to your assessment.';

    return (
      <Tooltip title={checkBoxTooltip} placement="right">
        <span>
          <CustomCheckbox
            disabled={isUnaligned}
            checked={selectedQuestions.includes(hit.reference)}
            onChange={handleCheckboxChange}
            checkboxSize="medium"
            centered
            ariaLabel={`Select item ${hit.reference}`}
          />
        </span>
      </Tooltip>
    );
  };

  const renderItem = () => {
    const questionType = hit['Question Type'] || '';
    const questionTypeName =
      questionType in LEARNOSITY_QUESTION_TYPE_NAMES
        ? LEARNOSITY_QUESTION_TYPE_NAMES[questionType]
        : questionType;
    return (
      <Box sx={{ py: 0 }}>
        <Typography
          variant="body2"
          component="div"
          sx={{
            fontSize: '14px',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            lineHeight: '1.2',
            mb: 1,
            fontFamily: "'Montserrat'",
          }}
        >
          <MathJaxItem data={hit.content} />
        </Typography>
        {questionTypeName !== '' && (
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: "'Montserrat'",
            }}
            aria-label={`Question Type: ${questionTypeName}`}
          >
            {questionTypeName}
          </Typography>
        )}
      </Box>
    );
  };

  const renderTagColumn = (columnKey: string) => {
    if (columnKey === 'Standard') {
      const abGuids = Array.isArray(hit['AB GUID']) ? hit['AB GUID'] : [];
      const standards = abGuids
        .map(getStandardByGuid)
        .filter(Boolean) as unknown as StandardRecord[];

      if (standards.length === 0) {
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              color: 'text.disabled',
              fontFamily: "'Montserrat'",
            }}
            aria-label={`${columnKey}: -`}
          >
            -
          </Typography>
        );
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '100%',
          }}
        >
          {standards.length > 0 &&
            standards.map((standard) => (
              <Tooltip
                key={standard.id}
                title={`${standard.code}: ${standard.description}`}
                placement="top"
              >
                <span>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '12px',
                      maxWidth: '100%',
                      display: 'inline-block',
                      color: standard.code !== '-' ? 'text.primary' : 'text.disabled',
                      cursor: 'help',
                      fontFamily: "'Montserrat'",
                    }}
                    aria-label={`${columnKey}: ${standard.code}`}
                  >
                    {standard.code}
                  </Typography>
                </span>
              </Tooltip>
            ))}
        </Box>
      );
    }

    const centeredColumns = [
      'Grade Levels',
      'Difficulty',
      'Blooms Taxonomy',
      'Depth Of Knowledge',
    ];

    // Special handling for Skill - always show full text, allow wrapping
    if (columnKey === 'Skill Display' || columnKey === 'Skill') {
      let skillNames = hit?.[columnKey];

      // if Skill Display/Skill doesn't exist, derive Skill Display from Skill
      if (!skillNames && columnKey === 'Skill Display') {
        skillNames = hit?.Skill?.map((name: string) => name.replace(/-/g, ' ')) || [];
      }
      if (!skillNames) {
        skillNames = [];
      }

      if (skillNames.length === 0) {
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              color: 'text.disabled',
              fontFamily: "'Montserrat'",
            }}
            aria-label={`${columnKey}: -`}
          >
            -
          </Typography>
        );
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '100%',
            whiteSpace: 'normal', // allow wrapping
            overflow: 'visible',
          }}
        >
          {skillNames.map((skillName: string) => (
            <Tooltip key={skillName} title={skillName} placement="top">
              <span>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '12px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    display: 'inline-block',
                    color: skillName !== '-' ? 'text.primary' : 'text.disabled',
                    fontFamily: "'Montserrat'",
                  }}
                  aria-label={`${columnKey}: ${skillName}`}
                >
                  {skillName}
                </Typography>
              </span>
            </Tooltip>
          ))}
        </Box>
      );
    }

    // Default case for other columns - single line with ellipsis
    const values = hit[columnKey as keyof AlgoliaLearnosityItem] || [];
    const value = Array.isArray(values) && values.length > 0 ? values[0] : '-';

    const isCentered = centeredColumns.includes(columnKey);

    let returnDiv;
    if (isCentered) {
      returnDiv = (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <Typography
            variant="body2"
            data-centered="true"
            sx={{
              fontSize: '12px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              display: 'inline-block',
              color: value !== '-' ? 'text.primary' : 'text.disabled',
              cursor: 'default',
              fontFamily: "'Montserrat'",
              textAlign: 'center',
            }}
            aria-label={`${columnKey}: ${value}`}
          >
            {value}
          </Typography>
        </Box>
      );
    }
    else {
      returnDiv = (
        <Tooltip title={value} placement="top">
          <span>
            <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              display: 'inline-block',
              color: value !== '-' ? 'text.primary' : 'text.disabled',
              cursor: 'help',
              fontFamily: "'Montserrat'",
            }}
            aria-label={`${columnKey}: ${value}`}
          >
            {value}
          </Typography>
        </span>
      </Tooltip>
      );
    }

    return returnDiv;
  };

  const renderTagButton = () => {
    // Define tag field candidates - these are the fields we expect to contain tags
    const tagFields = [
      'Question Type',
      'Language',
      'Blooms Taxonomy',
      'Depth Of Knowledge',
      'Difficulty',
      'Grade Levels',
      'Subject',
      'Skill',
      'Skill Display',
      'Standard',
    ];

    // Count only tag fields that exist in the hit and have values
    let tagCount = 0;
    tagFields.forEach((field) => {
      if (
        hit[field] &&
        Array.isArray(hit[field]) &&
        (hit[field] as string[]).length > 0
      ) {
        tagCount += 1;
      }
    });

    // If no tags were found but we're in demo mode, show a demo count
    const DEMO_MODE = true; // Set to false in production
    if (tagCount === 0 && DEMO_MODE) {
      tagCount = 9; // Updated to match the number of tags in the dialog
    }

    return (
      <Button
        variant="outlined"
        size="small"
        onClick={() => onTagsOpen(hit)}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          minWidth: '100px',
          maxWidth: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          fontFamily: "'Montserrat'",
        }}
        aria-label={`View all tags (${tagCount})`}
      >
        All ({tagCount})
      </Button>
    );
  };

  const renderPreview = () => <QuestionPreview referenceId={hit.reference} />;

  return {
    id: hit.reference,
    index: calculatedIndex,
    checkbox: renderCheckbox(),
    item: renderItem(),
    ...Object.fromEntries(
      selectedTagColumns.map((columnKey) => {
        const centeredColumns = [
          'Grade Levels',
          'Difficulty',
          'Blooms Taxonomy',
          'Depth Of Knowledge',
        ];
        const isCentered = centeredColumns.includes(columnKey);
        return [
          columnKey.toLowerCase(),
          <Box
            sx={
              isCentered
                ? {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                  }
                : {}
            }
          >
            {renderTagColumn(columnKey)}
          </Box>,
        ];
      })
    ),
    preview: selectButton ? selectButton(hit.reference) : renderPreview(),
    tags: renderTagButton(),
  };
};

export default AlgoliaTableRow;
